import { useEffect, useState, useMemo } from 'react';
import { App, Button, Drawer, Flex, Radio, Space, Typography } from 'antd';
import { useGetRuleFields } from '@/features/agreements/hooks/useRuleGroups';
import { getErrorMessage } from '@/shared/utils/errors/getErrorMessage';
import { LoadingIndicator } from '@/shared/components/LoadingIndicator';
import { ErrorMessage } from '@/shared/components/ErrorMessage';
import { RuleGroupEditor } from './RuleGroupEditor';
import {
  validateGroup,
  validateAllRulesAndGroups,
  transformChildrenFromDto,
  transformChildrenToDto,
} from './ruleGroupUtils';

const { Text } = Typography;

export const RulesDrawer = ({
  open,
  onClose,
  title = 'Definir reglas',
  description,
  extraContent,
  ruleGroup,
  onSave,
  submitButtonText = 'Guardar',
  width = '48em',
  rulesRequired = true,
}) => {
  const { notification } = App.useApp();
  const isEditMode = !!ruleGroup;

  const [operator, setOperator] = useState('and');
  const [rules, setRules] = useState([]);
  const [children, setChildren] = useState([]);
  const [submitAttempted, setSubmitAttempted] = useState(false);
  const [isSaving, setIsSaving] = useState(false);

  const ruleFieldsQuery = useGetRuleFields({ enabled: open });
  const ruleFields = ruleFieldsQuery.data ?? [];

  // Inicializar estado cuando se abre el drawer o cambia ruleGroup
  useEffect(() => {
    if (open) {
      if (isEditMode && ruleGroup) {
        setOperator(ruleGroup.operator || 'and');
        setRules(
          (ruleGroup.rules || []).map((rule) => {
            const fieldId = typeof rule.field === 'object' ? rule.field?.id : rule.field;
            return {
              ...(rule.id != null && { id: rule.id }),
              field: fieldId,
              operator: rule.operator,
              value: rule.value,
            };
          })
        );
        setChildren(transformChildrenFromDto(ruleGroup.children));
      } else {
        setOperator('and');
        setRules([]);
        setChildren([]);
      }
    }
  }, [open, isEditMode, ruleGroup]);

  const handleRootGroupChange = (updatedGroup) => {
    setOperator(updatedGroup.operator);
    setRules(updatedGroup.rules || []);
    setChildren(updatedGroup.children || []);
  };

  const handleSubmit = async () => {
    setSubmitAttempted(true);

    const rootGroup = {
      operator,
      rules,
      children,
    };

    const isEmpty = !validateGroup(rootGroup);
    if (rulesRequired && isEmpty) {
      notification.warning({
        message: <Text strong>Datos incompletos</Text>,
        description: 'Revisa los campos marcados y asegúrate de que cada grupo tenga al menos una regla o subgrupo válido.',
      });
      return;
    }

    if (!isEmpty && !validateAllRulesAndGroups(rootGroup)) {
      notification.warning({
        message: <Text strong>Datos incompletos</Text>,
        description: 'Revisa los campos marcados y asegúrate de que cada grupo tenga al menos una regla o subgrupo válido.',
      });
      return;
    }

    if (typeof onSave !== 'function') {
      return;
    }

    const ruleGroupPayload = {
      operator,
      rules: rules.map((rule) => ({
        ...(rule.id != null && { id: rule.id }),
        field: rule.field,
        operator: rule.operator,
        value: rule.value,
      })),
      children: transformChildrenToDto(children),
    };

    try {
      setIsSaving(true);
      await onSave({
        ruleGroupPayload,
        ruleGroupId: ruleGroup?.id,
      });
      setSubmitAttempted(false);
      handleClose();
    } catch (error) {
      if (error?.errorFields) return;
      notification.error({
        message: <Text strong>{isEditMode ? 'No se pudieron actualizar las reglas' : 'No se pudieron crear las reglas'}</Text>,
        description: getErrorMessage(error),
      });
    } finally {
      setIsSaving(false);
    }
  };

  const handleClose = () => {
    setOperator('and');
    setRules([]);
    setChildren([]);
    setSubmitAttempted(false);
    onClose?.();
  };

  if (ruleFieldsQuery.isLoading) {
    return (
      <Drawer
        title={title}
        open={open}
        onClose={handleClose}
        width={width}
        closable={{ placement: 'end' }}
        destroyOnClose
      >
        <LoadingIndicator />
      </Drawer>
    );
  }

  if (ruleFieldsQuery.isError) {
    return (
      <Drawer
        title={title}
        open={open}
        onClose={handleClose}
        width={width}
        closable={{ placement: 'end' }}
        destroyOnClose
      >
        <ErrorMessage error={ruleFieldsQuery.error} />
      </Drawer>
    );
  }

  return (
    <Drawer
      title={title}
      open={open}
      onClose={handleClose}
      width={width}
      closable={{ placement: 'end' }}
      destroyOnClose
      footer={
        <Flex gap='small' justify='end'>
          <Button onClick={handleClose}>Cancelar</Button>
          <Button type='primary' onClick={handleSubmit} loading={isSaving}>
            {submitButtonText}
          </Button>
        </Flex>
      }
    >
      <Flex vertical gap='large'>
        {description != null && (
          <Text type='secondary'>{description}</Text>
        )}

        {extraContent}

        <Flex vertical gap='middle'>
          <Text strong>¿Cuándo debe aplicarse?</Text>
          <Radio.Group value={operator} onChange={(e) => setOperator(e.target.value)}>
            <Space direction='vertical'>
              <Radio value='and'>Se deben cumplir todas las condiciones</Radio>
              <Radio value='or'>Se debe cumplir al menos una condición</Radio>
              <Radio value='not'>Se niega la condición</Radio>
            </Space>
          </Radio.Group>
        </Flex>

        <Flex vertical gap='middle'>
          <Text strong>Condiciones</Text>
          <RuleGroupEditor
            group={{ operator, rules, children }}
            ruleFields={ruleFields}
            onChange={handleRootGroupChange}
            onRemove={undefined}
            showFieldErrors={submitAttempted}
            validateGroup={validateGroup}
          />
        </Flex>
      </Flex>
    </Drawer>
  );
};
